/** @format */

import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';

import { skip, take, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { Store } from '@ngrx/store';

import {
	MLChannel,
	MLEvent,
	MLEventType,
	MLFacet,
	MLFilter,
	MLItem,
	MLItemType,
	MLResult,
	MLResultFlags,
	MLResultType,
	MLSearch,
	MLSearchType,
	MLStore,
	MLTrack
} from '@machine-learning/data-access';
import { KillswitchService, UtilityService, WINDOW } from '@shared/data-access/services';

import {
	selectDisplayableProductListData,
	selectProductList
} from '@plp/data-access/state/product-list.selector';
import {
	FacetVO,
	ProductVO,
	SearchVO,
	ProductList,
	PinnedContentSpot,
	MLClickEventParams,
	DisplayableProductListData,
	DisplayableProductListItem
} from '@plp/data-access/interfaces';
import { Chain, Environment, PromoteIQ } from '@shared/data-access/interfaces';
import { CATEGORY_FILTER_ID, FACET_STORE } from '@plp/data-access/constants';

@Injectable({
	providedIn: 'root'
})
export class ProductListMlService {
	private readonly mlV1EventsUrl = this.environment.api.mlEvents;
	private readonly track: MLTrack = this.getCustomerIdData();
	mlSearchEventParentUUID = signal<string>('');

	private chainAbbr = '';
	private currentMlSearch: MLSearch | null = null;
	private currentMlResultFlags: MLResultFlags | null = null;
	private resultTypeData: MLResultType | null;

	private featureFlags: Record<string, boolean> = {
		isPLPMLIntegrationEnabled: false
	};

	readonly productListData$ = this.store.select(selectProductList);
	readonly displayableProductListData$: Observable<DisplayableProductListData> =
		this.store.select(selectDisplayableProductListData);

	// Used to ensure PLP ML events are sent only once per page load for setCriteoProducts
	readonly plpListIsFirstRender = signal<boolean>(true);
	// Used to prevent multiple ML events from firing on pagination/sorting/filtering
	readonly updateDisplayableProductListActionType = signal<string>('');

	constructor(
		private readonly http: HttpClient,
		private readonly store: Store,
		private readonly utilityService: UtilityService,
		private readonly chain: Chain,
		private readonly killswitchService: KillswitchService,
		private readonly environment: Environment,
		@Inject(PLATFORM_ID) private platformId: string,
		@Inject(WINDOW) private window: Window
	) {
		this.chainAbbr = this.chain.chainIdentifierAbbr;
		this.checkKillswitch();
	}

	getMlSearchEventParentUUID(): string {
		if (!isPlatformBrowser(this.platformId)) {
			return '';
		}
		return this.mlSearchEventParentUUID();
	}

	generateNewMlSearchEventParentUUID(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		const newUuid = this.utilityService.generateUUID();
		this.mlSearchEventParentUUID.set(newUuid);
	}

	sendClickEventData(
		store: MLStore,
		selectedProduct: ProductVO,
		dpld: DisplayableProductListData,
		productList: ProductList,
		timeTaken: number,
		catgroupId: number | null,
		index: number
	): Promise<Response> {
		if (!this.featureFlags.isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		const clickEventId = this.utilityService.generateUUID();
		return this.buildAndRecordClickEvent({
			catgroupId,
			dpld,
			eventType: MLEventType.CLICK,
			id: clickEventId,
			productList,
			selectedProduct,
			selectedProductIndex: index,
			store,
			timeTaken
		});
	}

	sendImpressionEventData(
		itemGroup: MLItem[],
		facets: MLFacet[],
		chainAbbr: string,
		facetVOs: FacetVO[],
		totalCount: number
	): void {
		if (!this.featureFlags.isPLPMLIntegrationEnabled || itemGroup.length === 0) {
			return;
		}

		const updatedFacets = this.updateFacetCount(facets, facetVOs, totalCount);

		const individualImpressionUuid: string = this.utilityService.generateUUID();
		// Format the date to be YYYY-MM-DD
		const date: string = this.getYYYYMMDD();

		const rowData: MLEvent = {
			parentId: this.mlSearchEventParentUUID(),
			channel: MLChannel.WEB,
			id: individualImpressionUuid,
			result: {
				facets: updatedFacets,
				flags: this.currentMlResultFlags,
				items: itemGroup,
				took: 0,
				total: itemGroup.length,
				type: this.resultTypeData
			},
			store: MLStore[chainAbbr.toUpperCase()],
			track: this.track,
			type: MLEventType.IMPRESSION,
			date,
			search: this.currentMlSearch,
			source: '/v2/search',
			time: Date.now()
		};

		this.http
			.put(this.mlV1EventsUrl, rowData)
			.pipe(take(1))
			.subscribe({
				error: (err) => {
					of(err);
				}
			});
	}

	private updateFacetCount(
		facets: MLFacet[],
		facetVOs: FacetVO[],
		totalCount: number
	): MLFacet[] {
		if (facetVOs.length === 0) {
			return [...facets];
		}

		const updatedFacets = [...facets];

		const facetIdsToUpdate = [CATEGORY_FILTER_ID, FACET_STORE];

		facetIdsToUpdate.forEach((facetId) => {
			this.updateSingleFacetCount(updatedFacets, facetId, totalCount);
		});

		return updatedFacets;
	}

	private updateSingleFacetCount(facets: MLFacet[], facetId: string, totalCount: number): void {
		const index = facets.findIndex((facet) => facet.facet === facetId);
		if (index !== -1) {
			const facet = facets[index];
			facets[index] = {
				...facet,
				values: [{ ...facet.values[0], count: totalCount }]
			};
		}
	}

	sendEventPerType(items: MLItem[], productList: ProductList, eventType: MLEventType): void {
		const sVO: SearchVO = productList.searchVO;
		const store: MLStore = MLStore[this.chainAbbr.toUpperCase()];

		const facets: MLFacet[] = productList.facetVOs
			? productList.facetVOs.map((facet) => ({
					facet: facet.attrIdentifier,
					values: facet.valueList
				}))
			: [];
		const filters: MLFilter[] = this.buildMLFilters(sVO.selectedFilters);

		// All flags are optional, therefore provide false or empty string if not present
		const flags: MLResultFlags = this.buildResultFlags(productList);
		this.currentMlResultFlags = flags;

		const search: MLSearch = this.buildMLSearch(sVO, sVO.selectedCategory, filters);
		this.currentMlSearch = search;

		const date = this.getYYYYMMDD();

		const mlResultType: MLResultType | null = this.getResultType(sVO.isFamilyPage, productList);
		this.resultTypeData = mlResultType;

		// This is the request payload for the Page Search Event
		const pageEventData: Partial<MLEvent> = {
			channel: MLChannel.WEB,
			date,
			result: {
				facets,
				flags,
				items, // filter out depending on search (products and pinned), sponsored and pcs
				took: 0,
				total: productList.totalCount,
				type: mlResultType
			},
			search,
			source: '/v2/search',
			store,
			time: Date.now(),
			track: this.track,
			type: eventType // search, sponsored and pcs
		};

		if (eventType !== MLEventType.SEARCH) {
			pageEventData.id = this.utilityService.generateUUID();
			pageEventData.parentId = this.mlSearchEventParentUUID();
		} else {
			pageEventData.id = this.mlSearchEventParentUUID();
		}

		this.http.put(this.mlV1EventsUrl, pageEventData).subscribe({
			error: (err) => of(err)
		});
	}

	sendSearchEventData(): void {
		if (!this.featureFlags.isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		this.displayableProductListData$
			.pipe(skip(1), withLatestFrom(this.productListData$), take(1))
			.subscribe(([dpld, productList]: [DisplayableProductListData, ProductList]) => {
				const items: MLItem[] = dpld
					? dpld.map((product: DisplayableProductListItem, index: number) =>
							this.buildMLItem(product, index)
						)
					: [];

				const sponsoredProducts = items.filter(
					(item) => item.type === MLItemType.SPONSORED_PRODUCT
				);
				if (sponsoredProducts.length) {
					this.sendEventPerType(
						sponsoredProducts,
						productList,
						MLEventType.SPONSORED_PRODUCTS
					);
				}

				const pcsProducts = items.filter(
					(item) => item.type === MLItemType.PINNED_CONTENT_SPOT
				);
				if (pcsProducts.length) {
					this.sendEventPerType(
						pcsProducts,
						productList,
						MLEventType.PINNED_CONTENT_SPOTS
					);
				}

				const searchProducts = items.filter(
					(item) =>
						item.type === MLItemType.PRODUCT || item.type === MLItemType.PINNED_PRODUCT
				);
				this.sendEventPerType(searchProducts, productList, MLEventType.SEARCH);
			});
	}

	buildAndRecordClickEvent(params: MLClickEventParams): Promise<Response> {
		const {
			store,
			selectedProduct,
			dpld,
			productList,
			timeTaken,
			catgroupId,
			eventType,
			selectedProductIndex,
			id = ''
		} = params;
		if (!this.featureFlags.isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}

		const clickedProduct = this.buildMLItem(selectedProduct, selectedProductIndex);
		if (clickedProduct.type === MLItemType.PINNED_CONTENT_SPOT) {
			return;
		}

		const category: string = catgroupId !== null ? catgroupId.toString() : '';
		const results: SearchVO = productList.searchVO;
		const mlResultType: MLResultType | null = this.getResultType(
			productList.searchVO.isFamilyPage,
			productList
		);

		const selectedFilters = Object.keys(results.selectedFilters).length
			? Object.keys(results.selectedFilters).map((key) => ({
					facet: key,
					values: results.selectedFilters[key]
				}))
			: [];
		const mlSearch: MLSearch = this.buildMLSearch(results, category, selectedFilters);

		const items: MLItem[] = dpld.map(
			(item: DisplayableProductListItem, index: number): MLItem =>
				this.buildMLItem(item, index)
		);

		const facets: MLFacet[] = productList.facetVOs
			? productList.facetVOs.map(
					(facet: FacetVO): MLFacet => ({
						facet: facet.attrIdentifier,
						values: facet.valueList
					})
				)
			: [];
		const track: MLTrack = this.track;

		const flags: MLResultFlags = this.buildResultFlags(productList);
		const mlResult: MLResult = {
			facets,
			flags,
			items,
			took: timeTaken,
			total: results.totalCount,
			type: mlResultType
		};

		const date: string = this.getYYYYMMDD();

		const mlEvent: MLEvent = {
			channel: MLChannel.WEB,
			click: clickedProduct,
			date,
			id,
			parentId: this.mlSearchEventParentUUID(),
			result: mlResult,
			search: mlSearch,
			source: '/v2/search',
			store,
			time: Date.now(),
			track,
			type: eventType
		};

		return this.recordClickEvent(mlEvent);
	}

	buildMLItem(product: DisplayableProductListItem, index: number): MLItem | undefined {
		let type: MLItemType;
		let id = '';
		if ((product as ProductVO).parentPartnumber) {
			type = (product as ProductVO).isPinned ? MLItemType.PINNED_PRODUCT : MLItemType.PRODUCT;
			id = (product as ProductVO).parentPartnumber;
		} else if ((product as PromoteIQ).product_id) {
			type = MLItemType.SPONSORED_PRODUCT;
			id = (product as PromoteIQ).product_id;
		} else if ((product as PinnedContentSpot).csid) {
			const pcs: PinnedContentSpot = product as PinnedContentSpot;
			type = MLItemType.PINNED_CONTENT_SPOT;
			id = `${pcs.csid}-${pcs.pinnedPosition}`;
		}

		return {
			id,
			num: index + 1, // 1-based index
			type
		};
	}

	// fetch with keepalive to ensure the request is not terminated by navigation to PDP
	// Firefox version min. requirement is v133 (11-26-2024)
	private recordClickEvent(event: MLEvent): Promise<Response> {
		return fetch(this.mlV1EventsUrl, {
			method: 'PUT',
			body: JSON.stringify(event),
			headers: {
				['Content-Type']: 'application/json'
			},
			keepalive: true
		});
	}

	private buildMLSearch(
		results: SearchVO,
		category: string,
		selectedFilters: MLFilter[]
	): MLSearch {
		const term: string | null = results.isFamilyPage ? null : results.searchTerm;
		const type = results.isFamilyPage ? MLSearchType.PLP : MLSearchType.SRLP;
		return {
			category,
			filters: selectedFilters,
			page: results.pageNumber,
			size: results.pageSize,
			sort: results.selectedSort,
			term,
			type
		};
	}

	private buildMLFilters(selectedFilters: Record<string, string[]>): MLFilter[] {
		return Object.entries(selectedFilters)
			.filter(([key, values]) => key && values)
			.map(([facet, values]) => ({ facet, values }));
	}

	private buildResultFlags(productList: ProductList): MLResultFlags {
		return {
			mlBypass: productList.mlBypass,
			mlGroup: productList.mlGroup || '',
			mlRank: !!productList.mlRank,
			mlRewrite: !!productList.mlRewrite,
			mlSpelling: !!productList.mlSpelling
		};
	}

	private getResultType(isFamilyPage: boolean, productList: ProductList): MLResultType | null {
		if (isFamilyPage) {
			return null;
		}

		const didYouMeanVO = productList.didYouMeanVO || null;
		const fuzzyMatch = productList.fuzzyMatch || null;
		const priceFilterZero = productList.priceFilterZero || null;
		const myStoreZero = productList.myStoreZero || null;
		const landingPage = productList.searchVO.landingPageURL || null;
		let resultType = MLResultType.NORMAL;
		if (fuzzyMatch) {
			resultType = MLResultType.FUZZY;
		}
		if (didYouMeanVO) {
			resultType = MLResultType.DID_YOU_MEAN;
		}
		if (myStoreZero) {
			resultType = MLResultType.MY_STORE_ZERO;
		}
		if (priceFilterZero) {
			resultType = MLResultType.PRICE_FILTER_ZERO;
		}
		if (landingPage) {
			resultType = MLResultType.LANDING_PAGE;
		}

		return resultType;
	}

	private checkKillswitch(): void {
		const flags: string[] = Object.keys(this.featureFlags);
		this.killswitchService
			.getKillSwitchData$(flags)
			.pipe(take(1))
			.subscribe((data) => {
				this.featureFlags = data;
			});
	}

	private getCustomerIdData(): MLTrack {
		if (isPlatformBrowser(this.platformId) && this.window._dsgTag?.CustomerIDs) {
			const custIdProps = this.window._dsgTag.CustomerIDs;
			return {
				AdobeMCVID: custIdProps.AdobeMCVID,
				CustomerKey: custIdProps.CustomerKey,
				DeviceID: custIdProps.DeviceID,
				Loyalty: custIdProps.Loyalty,
				MCID: custIdProps.MCID,
				Webstore: custIdProps.Webstore
			};
		}
	}

	// Format the date to be YYYY-MM-DD with leading 0s if necessary
	private getYYYYMMDD(): string {
		const currentDate = new Date();
		// eslint-disable-next-line prefer-template
		const month =
			currentDate.getMonth() + 1 < 10
				? `0${currentDate.getMonth() + 1}`
				: currentDate.getMonth() + 1;
		// eslint-disable-next-line prefer-template
		const day =
			currentDate.getDate() < 10 ? `0${currentDate.getDate()}` : currentDate.getDate();
		// eslint-disable-next-line prefer-template
		return `${currentDate.getFullYear()}-${month}-${day}`;
	}
}
